<template>
  <div :class="['guideline-page', `guideline-page--${currentRole}`]">
    <AppSectionHero
      :title="pageContent.title"
      :subtitle="pageContent.subtitle"
      :show-refresh="false"
      :meta-label="pageContent.metaHeading"
      :meta-value="pageContent.updatedLabel"
    />

    <CCard class="border-0 shadow-sm shell">
      <CCardBody class="p-4">
        <div class="highlight-grid">
          <div class="highlight-card" v-for="item in pageContent.highlights" :key="item.title">
            <h3>{{ item.title }}</h3>
            <p>{{ item.copy }}</p>
          </div>
        </div>

        <div class="content-grid">
          <div class="panel-card">
            <h3>{{ pageContent.sectionTitles.workflow }}</h3>
            <div class="step-list">
              <div class="step-row" v-for="(item, index) in pageContent.workflow" :key="item">
                <span class="step-number">{{ index + 1 }}</span>
                <div class="step-copy">{{ item }}</div>
              </div>
            </div>
          </div>

          <div class="panel-card">
            <h3>{{ pageContent.sectionTitles.resources }}</h3>
            <div class="resource-list">
              <div class="resource-card" v-for="item in pageContent.resources" :key="item.title">
                <div class="resource-title">{{ item.title }}</div>
                <div class="resource-copy">{{ item.copy }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-card">
          <h3>{{ pageContent.sectionTitles.faq }}</h3>
          <div class="faq-list">
            <div class="faq-item" v-for="item in pageContent.faq" :key="item.question">
              <div class="faq-question">{{ item.question }}</div>
              <div class="faq-answer">{{ item.answer }}</div>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { detectRouteRole } from '@/utils/role-dashboard'

const CONTENT_BY_LOCALE = {
  en: {
    committee: {
      title: 'Committee Guideline',
      subtitle: 'Keep committee review steps, comment expectations, and final handoff rules easy to follow.',
      metaHeading: 'Current Version',
      updatedLabel: 'Committee Review Workflow v2.0',
      sectionTitles: { workflow: 'Workflow Steps', resources: 'Helpful Resources', faq: 'Frequently Asked Questions' },
      highlights: [
        { title: 'Review against the draft', copy: 'Always compare your comments with the actual uploaded PDF or draft sections before posting feedback.' },
        { title: 'Comment in threads', copy: 'Use article-specific comments so users and admins can trace exactly what the committee is referring to.' },
        { title: 'Finish with a final decision', copy: 'Only send the agreement to admin after the committee discussion is complete and the draft is satisfactory.' }
      ],
      workflow: [
        'Open a review assignment from the committee dashboard or review queue.',
        'Check the draft preview and confirm which pages, articles, or clauses need attention.',
        'Post as many committee comments as needed in the live discussion thread.',
        'Wait for the user to reply or resubmit the revised draft, then continue the same review thread.',
        'Use the final committee submit step to return the agreement to the user, recommend rejection, or send it to admin for final approval.'
      ],
      resources: [
        { title: 'Review Queue', copy: 'Start new assigned reviews and check which agreements need committee action first.' },
        { title: 'Review History', copy: 'Look back at previous committee decisions, comments, and completed review outcomes.' },
        { title: 'Live Draft Preview', copy: 'Use the draft preview panel to keep article comments tied to the exact content being reviewed.' }
      ],
      faq: [
        { question: 'Can the committee leave more than one comment?', answer: 'Yes. Committee members can keep adding comments in the live thread as many times as needed before the review is completed.' },
        { question: 'When should the committee send the draft to admin?', answer: 'Only after the committee is satisfied with the revised draft and is ready for admin final approval.' },
        { question: 'What happens if more changes are needed?', answer: 'Use the committee completion step to return the agreement to the user so the same review loop can continue.' }
      ]
    },
    admin: {
      title: 'Guideline',
      subtitle: 'Keep admin assignment steps, final approval rules, and reporting guidance easy to follow.',
      metaHeading: 'Current Version',
      updatedLabel: 'Admin Workflow v2.0',
      sectionTitles: { workflow: 'Workflow Steps', resources: 'Helpful Resources', faq: 'Frequently Asked Questions' },
      highlights: [
        { title: 'Assign the right committee', copy: 'Match each incoming agreement with the correct committee members before review begins.' },
        { title: 'Track the whole workflow', copy: 'Monitor movement from user submission to committee review and final admin decision.' },
        { title: 'Decide with traceability', copy: 'Use the full comment history and committee outcome before making the final approval or disapproval.' }
      ],
      workflow: [
        'Review newly submitted agreements and confirm the agreement details are complete.',
        'Assign committee members so review can start in the committee workspace.',
        'Wait for the committee to complete the live review and submit their result.',
        'Inspect the draft preview, committee thread, and final recommendation.',
        'Approve or disapprove the agreement and confirm the workflow status is updated for the user.'
      ],
      resources: [
        { title: 'All Agreements', copy: 'Browse full agreement records and track workflow status across the system.' },
        { title: 'Review Queue', copy: 'Check which documents need assignment, final decision, or follow-up.' },
        { title: 'Reports & Analytics', copy: 'Monitor agreement volume, geography, departments, and workflow movement.' }
      ],
      faq: [
        { question: 'What should happen before committee review starts?', answer: 'Admin should verify the agreement record and assign the correct committee members first.' },
        { question: 'Can admin approve before committee review is complete?', answer: 'No. Final approval should only happen after the committee has completed its review and submitted the draft back to admin.' },
        { question: 'What if admin disapproves an agreement?', answer: 'The agreement returns to revision so the user and committee can continue the workflow again as needed.' }
      ]
    }
  },
  th: {
    committee: {
      title: 'คู่มือคณะกรรมการ',
      subtitle: 'ทำให้ขั้นตอนการรีวิวของคณะกรรมการ ความคาดหวังต่อความคิดเห็น และกฎการส่งต่องานขั้นสุดท้ายเข้าใจได้ง่าย',
      metaHeading: 'เวอร์ชันปัจจุบัน',
      updatedLabel: 'เวิร์กโฟลว์การรีวิวของคณะกรรมการ v2.0',
      sectionTitles: { workflow: 'ขั้นตอนการทำงาน', resources: 'แหล่งข้อมูลที่เป็นประโยชน์', faq: 'คำถามที่พบบ่อย' },
      highlights: [
        { title: 'รีวิวโดยอ้างอิงจากร่างเอกสาร', copy: 'เปรียบเทียบความคิดเห็นของคุณกับ PDF หรือส่วนต่าง ๆ ของร่างเอกสารจริงทุกครั้งก่อนส่งความเห็น.' },
        { title: 'แสดงความคิดเห็นเป็นเธรด', copy: 'ใช้ความคิดเห็นแบบอ้างอิงรายมาตรา เพื่อให้ผู้ใช้และผู้ดูแลระบบติดตามได้ว่าคณะกรรมการกล่าวถึงส่วนใด.' },
        { title: 'จบด้วยการตัดสินใจที่ชัดเจน', copy: 'ส่งร่างกลับไปยังผู้ดูแลระบบเมื่อการอภิปรายของคณะกรรมการเสร็จสิ้นและร่างเอกสารอยู่ในสภาพพร้อมแล้วเท่านั้น.' }
      ],
      workflow: [
        'เปิดงานรีวิวจากแดชบอร์ดคณะกรรมการหรือคิวรีวิว.',
        'ตรวจสอบตัวอย่างร่างเอกสารและยืนยันว่าหน้า มาตรา หรือข้อกำหนดใดต้องได้รับความสนใจ.',
        'โพสต์ความคิดเห็นของคณะกรรมการในเธรดสนทนาได้ตามต้องการ.',
        'รอให้ผู้ใช้ตอบกลับหรือส่งร่างฉบับแก้ไข แล้วจึงดำเนินการต่อในเธรดรีวิวเดิม.',
        'ใช้ขั้นตอนส่งผลรีวิวขั้นสุดท้ายเพื่อส่งร่างกลับไปยังผู้ใช้ เสนอให้ปฏิเสธ หรือส่งต่อไปยังผู้ดูแลระบบเพื่ออนุมัติขั้นสุดท้าย.'
      ],
      resources: [
        { title: 'คิวรีวิว', copy: 'เริ่มงานรีวิวที่ได้รับมอบหมายและตรวจสอบว่าข้อตกลงใดต้องการการดำเนินการจากคณะกรรมการก่อน.' },
        { title: 'ประวัติการรีวิว', copy: 'ย้อนดูผลการตัดสินใจ ความคิดเห็น และผลลัพธ์ของการรีวิวที่เสร็จสิ้นแล้ว.' },
        { title: 'ตัวอย่างร่างแบบสด', copy: 'ใช้แผงตัวอย่างร่างเอกสารเพื่อผูกความคิดเห็นรายมาตราเข้ากับเนื้อหาที่กำลังรีวิวอย่างตรงจุด.' }
      ],
      faq: [
        { question: 'คณะกรรมการสามารถให้ความเห็นได้มากกว่าหนึ่งครั้งหรือไม่?', answer: 'ได้ คณะกรรมการสามารถเพิ่มความคิดเห็นในเธรดสนทนาแบบสดได้หลายครั้งเท่าที่จำเป็นก่อนปิดงานรีวิว.' },
        { question: 'ควรส่งร่างกลับไปยังผู้ดูแลระบบเมื่อใด?', answer: 'ควรส่งเมื่อคณะกรรมการพอใจกับร่างฉบับแก้ไขแล้วและพร้อมให้ผู้ดูแลระบบอนุมัติขั้นสุดท้าย.' },
        { question: 'ถ้ายังต้องมีการแก้ไขเพิ่มเติมจะเกิดอะไรขึ้น?', answer: 'ใช้ขั้นตอนปิดงานของคณะกรรมการเพื่อส่งข้อตกลงกลับไปยังผู้ใช้ เพื่อให้ลูปการรีวิวเดิมดำเนินต่อได้.' }
      ]
    },
    admin: {
      title: 'คู่มือ',
      subtitle: 'ทำให้ขั้นตอนการมอบหมายงานของผู้ดูแลระบบ กฎการอนุมัติขั้นสุดท้าย และแนวทางการรายงานเข้าใจได้ง่าย',
      metaHeading: 'เวอร์ชันปัจจุบัน',
      updatedLabel: 'เวิร์กโฟลว์ผู้ดูแลระบบ v2.0',
      sectionTitles: { workflow: 'ขั้นตอนการทำงาน', resources: 'แหล่งข้อมูลที่เป็นประโยชน์', faq: 'คำถามที่พบบ่อย' },
      highlights: [
        { title: 'มอบหมายคณะกรรมการให้เหมาะสม', copy: 'จับคู่ข้อตกลงใหม่แต่ละฉบับกับคณะกรรมการที่เหมาะสมก่อนเริ่มการรีวิว.' },
        { title: 'ติดตามเวิร์กโฟลว์ทั้งหมด', copy: 'ติดตามความเคลื่อนไหวตั้งแต่ผู้ใช้ส่งข้อตกลง จนถึงการรีวิวของคณะกรรมการและการตัดสินใจขั้นสุดท้ายของผู้ดูแลระบบ.' },
        { title: 'ตัดสินใจอย่างตรวจสอบย้อนหลังได้', copy: 'ใช้ประวัติความคิดเห็นทั้งหมดและผลลัพธ์จากคณะกรรมการก่อนตัดสินใจอนุมัติหรือไม่อนุมัติขั้นสุดท้าย.' }
      ],
      workflow: [
        'ตรวจสอบข้อตกลงที่เพิ่งส่งเข้ามาใหม่และยืนยันว่ารายละเอียดครบถ้วน.',
        'มอบหมายคณะกรรมการเพื่อให้เริ่มการรีวิวในพื้นที่ทำงานของคณะกรรมการได้.',
        'รอให้คณะกรรมการรีวิวแบบสดให้เสร็จและส่งผลกลับมา.',
        'ตรวจสอบตัวอย่างร่างเอกสาร เธรดความคิดเห็น และข้อเสนอแนะขั้นสุดท้าย.',
        'อนุมัติหรือไม่อนุมัติข้อตกลง และยืนยันว่าผู้ใช้เห็นสถานะเวิร์กโฟลว์ที่อัปเดตแล้ว.'
      ],
      resources: [
        { title: 'ข้อตกลงทั้งหมด', copy: 'เรียกดูข้อมูลข้อตกลงทั้งหมดและติดตามสถานะเวิร์กโฟลว์ทั่วทั้งระบบ.' },
        { title: 'คิวรีวิว', copy: 'ตรวจสอบว่าเอกสารใดต้องได้รับการมอบหมาย การตัดสินใจขั้นสุดท้าย หรือการติดตามผล.' },
        { title: 'รายงานและการวิเคราะห์', copy: 'ติดตามจำนวนข้อตกลง พื้นที่ทางภูมิศาสตร์ หน่วยงาน และความเคลื่อนไหวของเวิร์กโฟลว์.' }
      ],
      faq: [
        { question: 'ก่อนเริ่มการรีวิวของคณะกรรมการควรทำอะไรบ้าง?', answer: 'ผู้ดูแลระบบควรตรวจสอบข้อมูลข้อตกลงและมอบหมายคณะกรรมการที่ถูกต้องก่อน.' },
        { question: 'ผู้ดูแลระบบสามารถอนุมัติก่อนคณะกรรมการรีวิวเสร็จได้หรือไม่?', answer: 'ไม่ได้ การอนุมัติขั้นสุดท้ายควรเกิดขึ้นหลังจากคณะกรรมการรีวิวเสร็จและส่งร่างกลับมาแล้วเท่านั้น.' },
        { question: 'ถ้าผู้ดูแลระบบไม่อนุมัติข้อตกลงจะเกิดอะไรขึ้น?', answer: 'ข้อตกลงจะกลับเข้าสู่ขั้นแก้ไข เพื่อให้ผู้ใช้และคณะกรรมการดำเนินเวิร์กโฟลว์ต่อได้ตามความจำเป็น.' }
      ]
    }
  }
}

export default {
  name: 'GuidelinePage',
  components: { AppSectionHero },
  computed: {
    localeCode() {
      return this.$i18n && this.$i18n.locale === 'en' ? 'en' : 'th'
    },
    currentRole() {
      return detectRouteRole(this.$store, this.$route)
    },
    pageContent() {
      return CONTENT_BY_LOCALE[this.localeCode][this.currentRole === 'committee' ? 'committee' : 'admin']
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("guideline-page");
@include workspace-info-layout;

.guideline-page--committee .shell {
  background:
    radial-gradient(circle at top right, rgba(140, 21, 21, 0.1), transparent 22%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 249, 248, 0.98) 100%);
}

.guideline-page--committee .highlight-card::before {
  background: linear-gradient(90deg, #8c1515, #c2410c);
}

.guideline-page--committee .step-number {
  background: linear-gradient(135deg, #8c1515 0%, #b42318 100%);
  box-shadow: 0 10px 18px rgba(140, 21, 21, 0.2);
}

.guideline-page--committee .panel-card h3,
.guideline-page--committee .highlight-card h3,
.guideline-page--committee .resource-title,
.guideline-page--committee .faq-question {
  color: #7f1d1d;
}

.guideline-page--committee .resource-card {
  background: linear-gradient(180deg, #fff8f7 0%, #ffffff 100%);
  border-color: #efd4d1;
}

.guideline-page--committee .faq-item {
  background: linear-gradient(180deg, #fff9f8 0%, #ffffff 100%);
  border-color: #f1ddda;
}
</style>
